import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { SimpleGrid, Textarea } from "@chakra-ui/react"
import ErrorMessage from "src/components/ErrorMessage";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerExpense } from 'src/api/PartnerExpense';
import DateSelectController from '@/components/fields/DateSelect';
import {
    Select as ReactSelect
} from "chakra-react-select";
import { AmountInputController } from '@/components/fields/AmountInput';
import { useProjectNames } from '@/hooks/useProjectNames';
import { expenseService } from "@/service/PartnerExpenseService";
import { LabelAndValueOption } from "src/data/LabelAndValueOption";
import { partnerService } from "src/service/PartnerService";
import CurrencySelectController from "@/components/fields/CurrencySelect";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field"
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface ExpensesFormProps {
    expenseId?: number;
    organizationId: number;
    onSubmit: (ps: PartnerExpense) => Promise<void>;
    viewOnly?: boolean;
    header: string;
}

const ExpensesForm = ({ expenseId, organizationId, onSubmit, viewOnly, header }: ExpensesFormProps): JSX.Element => {
    const { t } = useI18n();
    const organization = useOrganization();
    const homeCurrency = organization?.currency;
    const { control, register, handleSubmit, reset, setValue, getValues, formState: { isSubmitting } } = useForm<PartnerExpense>({
        defaultValues: {
            id: expenseId,
            projectId: -1,
            partnerId: -1,
            amount: 0,
            currency: '',
            entryDate: new Date(),
            comments: '',
            organizationId: organizationId
        }
    });
    // loads the organization currency and configures the form with it
    useEffect(() => {
        if (!(expenseId > -1) && homeCurrency && !getValues('currency')) {
            setValue('currency', homeCurrency);
        }
    }, [expenseId, homeCurrency, getValues, setValue]);
    const [serverError, setServerError] = useState('');
    const { options: projectNames } = useProjectNames();
    const [partnerNames, setPartnerNames] = useState<LabelAndValueOption[]>([]); // unique names for institutes
    const navigate = useNavigate();

    useEffect(() => {
        if (expenseId > -1) {
            expenseService.find(expenseId).then(p => reset(p));
        }
    }, [expenseId, reset]);

    useEffect(() => {
        let b = partnerService.listAll().then(ns => {
            let ops = ns.map(n => {
                let o: LabelAndValueOption = {
                    label: n.name,
                    value: n.id
                }
                return o;
            });
            setPartnerNames(ops);
        });
        Promise.all([b]);
    }, []);

    const readOnly = (viewOnly ?? false) || !homeCurrency;
    // console.info(`form is readonly: ${readOnly}`);

    const onSubmitInternal = (c: PartnerExpense): Promise<void> => {
        console.info(`submitting ${JSON.stringify(c)}`);
        return onSubmit(c)
            .then(() => navigate('/partners/expenses'))
            .catch(_err => setServerError('Fehler'));
    };

    return (
        <FormPage title={header} description={t('forms.expenses.description')}
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                <CancelButton onClick={() => navigate('/partners/expenses/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError && <ErrorMessage message={serverError} />}
            <SectionPanel title={t('forms.expenses.details')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <Controller control={control} name='partnerId'
                        rules={{ required: "Partner auswählen", validate: (value) => value > -1 || 'Partner auswählen' }}
                        render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
                            <Field id="partnerId" label='Partner' required disabled={readOnly} invalid={invalid} errorText={error?.message}>
                                <ReactSelect
                                    isSearchable
                                    required
                                    isDisabled={readOnly}
                                    autoFocus={true}
                                    ref={ref}
                                    name={name}
                                    inputId='partnerId'
                                    onBlur={onBlur}
                                    placeholder='Partner'
                                    options={partnerNames}
                                    onChange={val => onChange(val.value)}
                                    value={partnerNames.find(c => c.value === value)}
                                />
                            </Field>
                        )} />

                    <ProjectSelectController control={control} projectNames={projectNames} name='projectId' label='Projekt' readOnly={readOnly} required/>
                </SimpleGrid>
                <DateSelectController label='Datum' required disabled={readOnly} control={control} name='entryDate' clearable={false} />
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <AmountInputController control={control} name='amount' readOnly={readOnly} />

                    <CurrencySelectController control={control} name='currency' readOnly={readOnly} />
                </SimpleGrid>

                <Field id="comments" label='Bemerkungen' disabled={readOnly}>
                    <Textarea {...register('comments')} minH='7rem' resize='vertical' />
                </Field>

            </SectionPanel>
        </FormPage>
    );
}

export default ExpensesForm
